import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { createGroup, deleteGroup, fetchGroups, type GroupListItem } from "../../api/groups";
import { ConfirmDialog } from "../../components/ConfirmDialog";
import { ApiError } from "../../lib/api";

// Same hard-coded customer scope Campaigns/List.tsx uses until multi-customer
// auth lands. Keep them in sync.
const CUSTOMER_ID = "00000000-0000-0000-0000-000000000001";

export function GroupsList() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ["groups"],
    queryFn: fetchGroups,
  });

  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const [confirmGroup, setConfirmGroup] = useState<{ id: string; name: string } | null>(null);
  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteGroup(id),
    onSuccess: () => {
      setConfirmGroup(null);
      void qc.invalidateQueries({ queryKey: ["groups"] });
    },
    onError: (err) =>
      alert(err instanceof ApiError ? err.message : "Failed to delete group."),
  });

  function resetCreateForm() {
    setName("");
    setDescription("");
    setCreateError(null);
  }

  const createMut = useMutation({
    mutationFn: () =>
      createGroup({
        customer_id: CUSTOMER_ID,
        name: name.trim(),
        description: description.trim() || null,
      }),
    onSuccess: (record) => {
      void qc.invalidateQueries({ queryKey: ["groups"] });
      setShowCreate(false);
      resetCreateForm();
      navigate(`/groups/${record.group_id}`);
    },
    onError: (err) => {
      setCreateError(err instanceof Error ? err.message : "Failed to create group.");
    },
  });

  const groups = data?.items ?? [];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Header */}
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 28,
            fontWeight: 600,
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          Target groups
        </h1>
        <span
          style={{
            color: "var(--text-muted)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
          }}
        >
          · {data?.total ?? groups.length} group{(data?.total ?? groups.length) === 1 ? "" : "s"}
        </span>
        <div style={{ flex: 1 }} />
        <button
          onClick={() => {
            setShowCreate((v) => !v);
            setCreateError(null);
          }}
          style={{
            background: "var(--accent)",
            color: "#fff",
            border: "1px solid var(--accent)",
            borderRadius: "var(--radius-input)",
            padding: "8px 16px",
            fontSize: 12,
            fontWeight: 600,
            cursor: "pointer",
            whiteSpace: "nowrap",
          }}
        >
          + New group
        </button>
      </div>

      <p
        style={{
          margin: 0,
          fontSize: 11.5,
          lineHeight: 1.5,
          color: "var(--text-muted)",
          maxWidth: 640,
        }}
      >
        Reusable, named sets of targets. A campaign full-mirrors a group&apos;s
        members while it&apos;s still a draft, then freezes the membership once it
        starts running.
      </p>

      {/* New-group form */}
      {showCreate && (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px solid var(--border)",
            borderRadius: 12,
            padding: 16,
            display: "flex",
            gap: 10,
            alignItems: "flex-end",
            flexWrap: "wrap",
          }}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 1, minWidth: 180 }}>
            <FieldLabel>Name *</FieldLabel>
            <input
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setCreateError(null);
              }}
              placeholder="e.g. Finance leadership"
              autoFocus
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) createMut.mutate();
              }}
              style={createInputStyle()}
            />
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 4, flex: 2, minWidth: 160 }}>
            <FieldLabel>Description</FieldLabel>
            <input
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setCreateError(null);
              }}
              placeholder="optional"
              onKeyDown={(e) => {
                if (e.key === "Enter" && name.trim()) createMut.mutate();
              }}
              style={createInputStyle()}
            />
          </div>
          <button
            disabled={!name.trim() || createMut.isPending}
            onClick={() => createMut.mutate()}
            style={{
              padding: "7px 16px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--accent)",
              background: !name.trim() ? "var(--bg-tertiary)" : "var(--accent)",
              color: !name.trim() ? "var(--text-muted)" : "white",
              cursor: !name.trim() ? "not-allowed" : "pointer",
              whiteSpace: "nowrap",
            }}
          >
            {createMut.isPending ? "Creating..." : "Create"}
          </button>
          <button
            onClick={() => {
              setShowCreate(false);
              resetCreateForm();
            }}
            style={{
              padding: "7px 12px",
              borderRadius: 8,
              fontSize: 11,
              fontWeight: 600,
              border: "1px solid var(--border)",
              background: "transparent",
              color: "var(--text-muted)",
              cursor: "pointer",
              whiteSpace: "nowrap",
            }}
          >
            Cancel
          </button>
          {createError && (
            <div
              style={{
                width: "100%",
                fontSize: 11,
                color: "var(--error)",
                fontFamily: "var(--font-mono)",
                letterSpacing: "0.04em",
              }}
            >
              {createError}
            </div>
          )}
        </div>
      )}

      {/* List */}
      {isLoading ? (
        <div style={{ fontSize: 12, color: "var(--text-muted)" }}>Loading…</div>
      ) : groups.length === 0 ? (
        <div
          style={{
            background: "var(--bg-card)",
            border: "1px dashed var(--border)",
            borderRadius: 12,
            padding: 24,
            textAlign: "center",
            fontSize: 12,
            color: "var(--text-muted)",
          }}
        >
          No groups yet. Create one above to start building reusable target sets.
        </div>
      ) : (
        <div
          style={{
            background: "var(--bg-card)",
            backdropFilter: "blur(var(--glass-blur))",
            border: "1px solid var(--glass-border)",
            borderRadius: 12,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <ListHeader />
          {groups.map((g) => (
            <GroupRow
              key={g.group_id}
              group={g}
              onClick={() => navigate(`/groups/${g.group_id}`)}
              onDelete={() => setConfirmGroup({ id: g.group_id, name: g.name })}
            />
          ))}
        </div>
      )}

      <ConfirmDialog
        open={confirmGroup !== null}
        title="Delete group?"
        body={
          <>
            Delete <strong>{confirmGroup?.name}</strong>? Its members are not deleted —
            they move to Ungrouped. This cannot be undone.
          </>
        }
        busy={deleteMut.isPending}
        onConfirm={() => confirmGroup && deleteMut.mutate(confirmGroup.id)}
        onCancel={() => setConfirmGroup(null)}
      />
    </div>
  );
}

function ListHeader() {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 2fr 0.8fr 1fr 36px",
        gap: 10,
        padding: "8px 14px",
        fontSize: 9.5,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: "var(--text-muted)",
        fontWeight: 700,
        background: "var(--bg-secondary)",
        borderBottom: "1px solid var(--border-subtle)",
      }}
    >
      <span>Name</span>
      <span>Description</span>
      <span>Members</span>
      <span>Created</span>
      <span />
    </div>
  );
}

function GroupRow({
  group,
  onClick,
  onDelete,
}: {
  group: GroupListItem;
  onClick: () => void;
  onDelete: () => void;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        display: "grid",
        gridTemplateColumns: "1.4fr 2fr 0.8fr 1fr 36px",
        gap: 10,
        padding: "10px 14px",
        borderBottom: "1px solid var(--border-subtle)",
        alignItems: "center",
        fontSize: 11.5,
        cursor: "pointer",
        transition: "background 0.1s",
      }}
    >
      <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>{group.name}</span>
      <span
        style={{
          color: "var(--text-secondary)",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
      >
        {group.description ?? "—"}
      </span>
      <span style={{ fontFamily: "var(--font-mono)", color: "var(--text-secondary)" }}>
        {group.member_count}
      </span>
      <span style={{ color: "var(--text-muted)", fontSize: 11 }}>
        {new Date(group.created_at).toLocaleDateString()}
      </span>
      <button
        type="button"
        aria-label={`Delete ${group.name}`}
        title="Delete group"
        onClick={(e) => {
          e.stopPropagation();
          onDelete();
        }}
        style={{
          display: "grid",
          placeItems: "center",
          width: 28,
          height: 28,
          borderRadius: 6,
          border: "1px solid var(--border-subtle)",
          background: "transparent",
          color: "var(--error)",
          cursor: "pointer",
          fontSize: 13,
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

// ─── Bits ─────────────────────────────────────────────────────────────────────────

function FieldLabel({ children }: { children: string }) {
  return (
    <span
      style={{
        fontSize: 9.5,
        fontWeight: 600,
        textTransform: "uppercase",
        letterSpacing: 0.6,
        color: "var(--text-muted)",
      }}
    >
      {children}
    </span>
  );
}

function createInputStyle(mono = false): React.CSSProperties {
  return {
    background: "var(--bg-input)",
    border: "1px solid var(--border)",
    borderRadius: 8,
    padding: "7px 10px",
    color: "var(--text-primary)",
    fontSize: 12,
    fontFamily: mono ? "var(--font-mono)" : "var(--font-sans)",
    outline: "none",
  };
}
